import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, post, put } from "@/lib/api";
import { FieldError } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = { name: "", description: "", price: "", max_locksmiths: "", max_runs_per_month: "", is_default: false, basic: false, featured: false, display_only: false, price_text: "", features: "" };
const LIMITS = [["max_locksmiths", "Maximum locksmiths"], ["max_runs_per_month", "Maximum warrant runs a month"]];
const toForm = (p) => (p ? { ...p, basic: !!p.basic, featured: !!p.featured, display_only: !!p.display_only, price_text: p.price_text || "", features: (p.features || []).join("\n"),
  price: p.price == null ? "" : String(p.price), max_locksmiths: p.max_locksmiths ?? "", max_runs_per_month: p.max_runs_per_month ?? "" } : blank);
const whole = (v) => /^\d*$/.test(String(v).trim()) && (String(v).trim() === "" || Number(v) >= 1);
const lines = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean);

function validate(f) {
  const errs = {};
  if (f.name.trim().length < 2) errs.name = "Enter a plan name";
  if (!(Number(f.price) > 0) && !(f.display_only && String(f.price).trim() === "")) errs.price = f.display_only ? "Enter a price above £0, or leave it blank" : "Enter a monthly price above £0";
  if (f.price_text.trim().length > 40) errs.price_text = "Keep the price text to 40 characters";
  const bullets = lines(f.features);
  if (bullets.length > 12 || bullets.some((b) => b.length > 120)) errs.features = "Up to 12 bullet points, each up to 120 characters";
  if (f.display_only && f.is_default) errs.form = "A show-only plan can't be the default for new sign-ups";
  LIMITS.forEach(([k]) => { if (!whole(f[k])) errs[k] = "Enter a whole number, or leave blank for unlimited"; });
  return errs;
}

export const PlanDialog = ({ open, onOpenChange, plan, onSaved }) => {
  const [f, setF] = useState(blank);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setF(toForm(plan)); setErrors({}); }
  }, [open, plan]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    const lim = (v) => (String(v).trim() === "" ? null : Number(v));
    const data = { name: f.name.trim(), description: f.description.trim(), price: String(f.price).trim() === "" ? null : Number(f.price), is_default: f.is_default, basic: f.basic, featured: f.featured,
      display_only: f.display_only, price_text: f.price_text.trim(), features: lines(f.features),
      max_locksmiths: lim(f.max_locksmiths), max_runs_per_month: lim(f.max_runs_per_month) };
    try {
      await (plan ? put(`/platform/plans/${plan.id}`, data) : post("/platform/plans", data));
      toast.success(plan ? "Plan updated" : "Plan created");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setErrors({ form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="plan-dialog">
        <DialogHeader><DialogTitle className="font-display">{plan ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <Label htmlFor="pl-name">Name *</Label>
            <Input id="pl-name" value={f.name} onChange={set("name")} className="mt-1.5" data-testid="plan-name-input" />
            <FieldError message={errors.name} testId="plan-name-error" />
          </div>
          <div>
            <Label htmlFor="pl-desc">Short description</Label>
            <Textarea id="pl-desc" rows={2} value={f.description} onChange={set("description")} className="mt-1.5" data-testid="plan-description-input" />
          </div>
          <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <Checkbox checked={f.display_only} onCheckedChange={(v) => { setF({ ...f, display_only: !!v, is_default: v ? false : f.is_default }); setErrors({}); }} className="mt-0.5" data-testid="plan-display-only-checkbox" />
            <span><span className="font-medium text-slate-900">Show only (no online sign-up)</span><span className="block text-xs text-slate-500">Shown on the homepage and Billing page with a "Contact us" button instead of sign-up. Nobody can subscribe to it online.</span></span>
          </label>
          <div>
            <Label htmlFor="pl-price">Monthly price (£){f.display_only ? "" : " *"}</Label>
            <Input id="pl-price" type="number" min="0.01" step="0.01" value={f.price} onChange={set("price")} className="mt-1.5" data-testid="plan-price-input" />
            <FieldError message={errors.price} testId="plan-price-error" />
            {plan && !f.display_only && <p className="mt-1 text-xs text-slate-500">Price changes apply to new subscriptions. Existing companies keep their current price.</p>}
          </div>
          <div>
            <Label htmlFor="pl-price-text">Price text</Label>
            <Input id="pl-price-text" maxLength={40} placeholder={'e.g. "Custom pricing" or "From £199"'} value={f.price_text} onChange={set("price_text")} className="mt-1.5" data-testid="plan-price-text-input" />
            <p className="mt-1 text-xs text-slate-500">Shown instead of "£/month" on the price cards. Leave blank to show the monthly price.</p>
            <FieldError message={errors.price_text} testId="plan-price-text-error" />
          </div>
          <div>
            <Label htmlFor="pl-features">Bullet points</Label>
            <Textarea id="pl-features" rows={4} placeholder={"One per line, e.g.\nUnlimited locksmiths\nPriority support"} value={f.features} onChange={set("features")} className="mt-1.5" data-testid="plan-features-input" />
            <p className="mt-1 text-xs text-slate-500">Replaces the standard list (limits, maps, free trial) on the price cards. Leave blank to use the standard list.</p>
            <FieldError message={errors.features} testId="plan-features-error" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {LIMITS.map(([k, label]) => (
              <div key={k}>
                <Label htmlFor={`pl-${k}`}>{label}</Label>
                <Input id={`pl-${k}`} type="number" min="1" placeholder="Unlimited" value={f[k]} onChange={set(k)} className="mt-1.5" data-testid={`plan-${k}-input`} />
                <FieldError message={errors[k]} testId={`plan-${k}-error`} />
              </div>
            ))}
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <Checkbox checked={f.basic} onCheckedChange={(v) => setF({ ...f, basic: !!v })} className="mt-0.5" data-testid="plan-basic-checkbox" />
            <span><span className="font-medium text-slate-900">Basic</span><span className="block text-xs text-slate-500">Turns off maps (dashboard map, Map page and run maps) for companies on this plan.</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <Checkbox checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: !!v })} className="mt-0.5" data-testid="plan-featured-checkbox" />
            <span><span className="font-medium text-slate-900">Most popular</span><span className="block text-xs text-slate-500">Highlights this plan on the homepage and Billing page. Only one plan can be featured.</span></span>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={f.is_default} disabled={f.display_only} onCheckedChange={(v) => setF({ ...f, is_default: !!v })} data-testid="plan-default-checkbox" />
            Default plan for new sign-ups
          </label>
          {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="plan-form-error">{errors.form}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="plan-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="plan-save-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save plan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
